import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, MessageSquare, Menu, X, ShieldCheck } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import MobileStickyCta from "./MobileStickyCta";

const UtilityBar = () => (
  <div
    data-testid="utility-bar"
    className="bg-yellow-500 text-yellow-950 border-b-2 border-yellow-600"
  >
    <div className="max-w-7xl mx-auto px-4 lg:px-10 h-9 flex items-center justify-between gap-3 text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em]">
      <div className="flex items-center gap-3 sm:gap-5 min-w-0">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <span className="inline-block h-2 w-2 rounded-full bg-green-700" />
          Free Estimates
        </span>
        <span className="hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap">
          <ShieldCheck className="h-3.5 w-3.5" /> Eau Claire, WI · Locally Owned
        </span>
      </div>
      <div className="flex items-center gap-3 sm:gap-4 shrink-0">
        <a
          href="tel:7152146563"
          data-testid="utility-call"
          className="inline-flex items-center gap-1.5 hover:underline whitespace-nowrap"
        >
          <Phone className="h-3.5 w-3.5" />
          <span className="hidden xs:inline">Call</span>
          <span>(715) 214-6563</span>
        </a>
        <a
          href="sms:+17152146563"
          data-testid="utility-text"
          className="hidden sm:inline-flex items-center gap-1.5 hover:underline whitespace-nowrap"
        >
          <MessageSquare className="h-3.5 w-3.5" /> Text Us
        </a>
      </div>
    </div>
  </div>
);

const navItems = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/book", label: "Book" },
];

export const SiteNav = () => {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-40"
    >
      <UtilityBar />
      <div className="backdrop-blur-xl bg-stone-900/85 border-b border-stone-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link">
          <BrandLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm uppercase tracking-[0.2em] font-bold transition-colors ${
                  isActive ? "text-yellow-400" : "text-stone-300 hover:text-yellow-300"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="sms:+17152146563"
            data-testid="nav-text-link"
            className="hidden sm:inline-flex items-center gap-2 text-stone-300 hover:text-yellow-300 text-sm font-semibold"
          >
            <MessageSquare className="h-4 w-4" /> Text
          </a>
          <a
            href="tel:7152146563"
            data-testid="nav-phone-link"
            className="hidden sm:inline-flex items-center gap-2 text-stone-300 hover:text-yellow-300 text-sm font-semibold"
          >
            <Phone className="h-4 w-4" /> 715-214-6563
          </a>
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="hidden md:inline-flex bg-yellow-500 text-yellow-950 font-bold uppercase tracking-wider px-5 py-2.5 border-2 border-yellow-500 hover:bg-yellow-400 transition-colors text-sm"
          >
            Free Estimate
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-stone-200 p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-800 bg-stone-900/95">
          <div className="px-6 py-4 flex flex-col gap-3">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-2 text-sm uppercase tracking-[0.2em] font-bold ${
                    isActive ? "text-yellow-400" : "text-stone-300"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="nav-mobile-book-cta"
              className="mt-2 bg-yellow-500 text-yellow-950 font-bold uppercase tracking-wider px-5 py-3 border-2 border-yellow-500 text-center text-sm"
            >
              Book Consultation
            </Link>
            <a
              href="tel:7152146563"
              className="text-stone-300 inline-flex items-center gap-2 text-sm"
            >
              <Phone className="h-4 w-4" /> 715-214-6563
            </a>
          </div>
        </div>
      )}
      </div>
    </header>
  );
};

export const SiteFooter = () => (
  <footer
    data-testid="site-footer"
    className="border-t border-stone-800 bg-stone-950 text-stone-300"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-10">
      <div>
        <BrandLogo />
        <p className="mt-4 text-sm text-stone-400 max-w-xs leading-relaxed">
          We specialize in property cleanup, debris hauling, landscaping
          support, storm recovery, and general property maintenance throughout
          Eau Claire and surrounding communities.
        </p>
      </div>
      <div>
        <div className="label-eyebrow mb-3">Contact</div>
        <a href="tel:7152146563" className="block text-stone-100 hover:text-yellow-300 font-semibold">
          715-214-6563
        </a>
        <a href="mailto:oz@ozends.com" className="block text-stone-400 hover:text-yellow-300 text-sm mt-1">
          oz@ozends.com
        </a>
        <p className="mt-3 text-sm text-stone-400">Eau Claire, WI · Travel by approved inquiry</p>
      </div>
      <div>
        <div className="label-eyebrow mb-3">Get Started</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/book" className="text-stone-100 hover:text-yellow-300">Book a consultation</Link></li>
          <li><Link to="/services" className="text-stone-300 hover:text-yellow-300">View services</Link></li>
          <li><Link to="/admin/login" className="text-stone-500 hover:text-yellow-300 text-xs">Admin</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-stone-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row gap-2 items-center justify-between text-xs text-stone-500">
        <div>© {new Date().getFullYear()} Oz and Ends LLC. All rights reserved.</div>
        <div className="brick-row" aria-hidden="true">
          <span /><span /><span /><span /><span /><span /><span /><span />
        </div>
      </div>
    </div>
  </footer>
);

export const SiteLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-stone-950">
    <SiteNav />
    <main className="flex-1 pb-20 md:pb-0">{children}</main>
    <SiteFooter />
    <MobileStickyCta />
  </div>
);

export default SiteLayout;
